const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * Sticky "Claim offer" bar (snippets/biopaw-sticky-atc.liquid).
 *
 * Shows once the main Add to Cart block has scrolled off the top of the screen (so the bundle
 * offer above it is gone too) and hides while the footer is on screen. Tapping it scrolls back up so
 * the Kaching bundle section sits in the middle of the screen, ready for the shopper to pick.
 * The delivery date is worked out on the shopper's device.
 */
class BpStickyAtc extends HTMLElement {
  #pastOffer = false;
  #footerVisible = false;
  /** @type {IntersectionObserver[]} */
  #observers = [];

  connectedCallback() {
    // Live at the top level of the page so no wrapper can stop position: fixed from sticking
    // to the screen. Drop any copy left behind when the product section re-renders.
    if (this.parentElement !== document.body) {
      for (const old of document.querySelectorAll('body > bp-sticky-atc')) {
        if (old.dataset.targetBlock === this.dataset.targetBlock) old.remove();
      }
      document.body.append(this);
      return;
    }

    this.#renderDate();
    this.querySelector('.bp-sticky-atc__button')?.addEventListener('click', this.#onClick);

    const block = document.getElementById(this.dataset.targetBlock || '');
    if (block) {
      const blockObserver = new IntersectionObserver(([entry]) => {
        if (!entry) return;
        this.#pastOffer = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.#update();
      });
      blockObserver.observe(block);
      this.#observers.push(blockObserver);
    }

    const footer = document.querySelector('footer');
    if (footer) {
      const footerObserver = new IntersectionObserver(([entry]) => {
        this.#footerVisible = Boolean(entry?.isIntersecting);
        this.#update();
      });
      footerObserver.observe(footer);
      this.#observers.push(footerObserver);
    }
  }

  disconnectedCallback() {
    this.querySelector('.bp-sticky-atc__button')?.removeEventListener('click', this.#onClick);
    for (const observer of this.#observers) observer.disconnect();
    this.#observers = [];
  }

  #update() {
    const visible = this.#pastOffer && !this.#footerVisible;
    this.classList.toggle('is-visible', visible);
    this.toggleAttribute('inert', !visible);
    this.setAttribute('aria-hidden', String(!visible));
  }

  #onClick = () => {
    const offer = this.#findOffer();
    if (!offer) return;

    const rect = offer.getBoundingClientRect();
    const headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 0;
    // Centre the bundle section; if it's taller than the space below the header, line its top up
    // under the header instead so the first option isn't cut off.
    const centred = window.scrollY + rect.top - (window.innerHeight - rect.height) / 2;
    const topAligned = window.scrollY + rect.top - headerHeight - 12;
    window.scrollTo({ top: Math.max(0, Math.min(centred, topAligned)), behavior: 'smooth' });
  };

  /** The visible Kaching bundle block, falling back to the main Add to Cart block. */
  #findOffer() {
    for (const el of document.querySelectorAll('kaching-bundles-block')) {
      if (el.getClientRects().length > 0) return el;
    }
    return document.getElementById(this.dataset.targetBlock || '');
  }

  #renderDate() {
    const dateEl = this.querySelector('.bp-sticky-atc__date');
    if (!dateEl) return;

    const date = new Date();
    date.setDate(date.getDate() + (Number(this.dataset.deliveryDays) || 3));
    dateEl.textContent = `${DAYS[date.getDay()]}, ${date.getDate()} ${MONTHS[date.getMonth()]}`;
  }
}

if (!customElements.get('bp-sticky-atc')) customElements.define('bp-sticky-atc', BpStickyAtc);
